import React from "react";
import { ErrorBanner } from "./features/player/ErrorBanner";
import type { UIError } from "./errors/types";

// React の render ツリー内で発生した想定外例外を捕捉し、ProblemDetail を
// 介さず「UIError.kind = unknown」相当のフォールバック UI に差し替える。
// Requirements 6.5: 未分類の例外もユーザーに明示したうえで復帰導線を提供する。

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // 開発者向けログ。ユーザーに露出しない形で原因特定に必要な情報を残す。
    console.error("[ErrorBoundary]", error, info);
  }

  private readonly handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const uiError: UIError = {
        kind: "unknown",
        title: "予期しないエラーが発生しました",
        body:
          this.state.error?.message ??
          "アプリの再読み込みをお試しください。",
        retryable: true,
        requiresRestart: false,
      };
      return <ErrorBanner error={uiError} onRetry={this.handleReset} />;
    }
    return this.props.children;
  }
}
